import React, { Component } from 'react';
import Modal from 'react-modal';
import profileImg from '../../../../utils/images/profileImg.png';
import BudImage from '../../../../utils/images/Bud.png';
import LanguageContext, { LanguageProvider } from '../../../../utils/common/LanguageContext';
import { toDisplayName } from '../../../../utils/common/NamingTerms';

// Active-speaker detection tuning
// Smoothed (logarithmic) audio level above which a participant is treated as "speaking".
const SPEAKING_THRESHOLD = 0.2;
// Keep the indicator visible for a short window after audio drops so it does not flicker
// during natural pauses between words.
const SPEAKING_OFF_DELAY = 600;
// Periodically re-check for subscribers that were created after this panel mounted
// (e.g. a participant who joins while the list is open).
const ATTACH_RETRY_INTERVAL = 1000;

// Higher-Order Component to inject translation function with ref forwarding
const withLanguage = (WrappedComponent) => {
    class WithLanguage extends Component {
        render() {
            const { forwardedRef, ...rest } = this.props;
            return (
                <LanguageContext.Consumer>
                    {({ t }) => <WrappedComponent {...rest} t={t} ref={forwardedRef} />}
                </LanguageContext.Consumer>
            );
        }
    }

    return React.forwardRef((props, ref) => {
        return <WithLanguage {...props} forwardedRef={ref} />;
    });
};

export class ParticipantContent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            removeStudentAlert: false,
            studentName: '',
            streamId: '',
            studentId: '',
            // Map of streamId -> true for participants currently speaking.
            speakingStreams: {},
        };
        // streamId -> { subscriber, handler } for attached audioLevelUpdated listeners.
        this.audioLevelHandlers = new Map();
        // streamId -> smoothed audio level (moving average).
        this.movingAverages = new Map();
        // streamId -> timeout that turns the indicator off after a pause.
        this.speakingOffTimers = new Map();
    }

    componentDidMount() {
        // Attach immediately and keep watching for late-arriving subscribers/streams.
        this.syncAudioLevelListeners();
        this.attachInterval = setInterval(() => {
            this.syncAudioLevelListeners();
        }, ATTACH_RETRY_INTERVAL);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.streams !== this.props.streams || prevProps.session !== this.props.session) {
            this.syncAudioLevelListeners();
        }
    }

    componentWillUnmount() {
        if (this.attachInterval) {
            clearInterval(this.attachInterval);
            this.attachInterval = null;
        }
        this.speakingOffTimers.forEach((timerId) => clearTimeout(timerId));
        this.speakingOffTimers.clear();
        this.audioLevelHandlers.forEach(({ subscriber, handler }) => {
            try {
                subscriber.off('audioLevelUpdated', handler);
            } catch (e) {
                // Subscriber may already be destroyed; ignore.
            }
        });
        this.audioLevelHandlers.clear();
        this.movingAverages.clear();
    }

    // Return the id used to key a stream throughout this component.
    getStreamKey = (stream) => stream && (stream.streamId || stream.id);

    // Attach audioLevelUpdated listeners to every remote stream's subscriber, and
    // detach any that belong to streams that are no longer present.
    syncAudioLevelListeners = () => {
        const { session, streams } = this.props;
        if (
            !session ||
            typeof session.getSubscribersForStream !== 'function' ||
            !Array.isArray(streams)
        ) {
            return;
        }

        const activeKeys = new Set();

        streams
            .filter((stream) => stream && stream.name !== 'screen')
            .forEach((stream) => {
                const streamKey = this.getStreamKey(stream);
                if (!streamKey) {
                    return;
                }
                activeKeys.add(streamKey);

                // Already listening for this stream.
                if (this.audioLevelHandlers.has(streamKey)) {
                    return;
                }

                let subscribers = [];
                try {
                    subscribers = session.getSubscribersForStream(stream) || [];
                } catch (e) {
                    subscribers = [];
                }
                const subscriber = subscribers[0];
                if (!subscriber || typeof subscriber.on !== 'function') {
                    return; // Subscriber not ready yet; the retry interval will pick it up.
                }

                const handler = (event) => this.handleAudioLevel(streamKey, event.audioLevel);
                subscriber.on('audioLevelUpdated', handler);
                this.audioLevelHandlers.set(streamKey, { subscriber, handler });
            });

        // Detach listeners for streams that have gone away.
        this.audioLevelHandlers.forEach(({ subscriber, handler }, streamKey) => {
            if (!activeKeys.has(streamKey)) {
                try {
                    subscriber.off('audioLevelUpdated', handler);
                } catch (e) {
                    // ignore
                }
                this.audioLevelHandlers.delete(streamKey);
                this.movingAverages.delete(streamKey);
                this.clearSpeaking(streamKey);
            }
        });
    };

    // Convert a raw OpenTok audio level (0-1, non-linear) into a smoothed, perceptual
    // value and decide whether the participant is speaking. Follows Vonage's recommended
    // moving-average + logarithmic-scaling approach.
    handleAudioLevel = (streamKey, audioLevel) => {
        const level = typeof audioLevel === 'number' ? audioLevel : 0;
        let avg = this.movingAverages.get(streamKey);
        if (avg == null || avg <= level) {
            avg = level;
        } else {
            avg = 0.7 * avg + 0.3 * level;
        }
        this.movingAverages.set(streamKey, avg);

        let logLevel = Math.log(avg) / Math.LN10 / 1.5 + 1;
        logLevel = Math.min(Math.max(logLevel, 0), 1);

        if (logLevel > SPEAKING_THRESHOLD) {
            this.markSpeaking(streamKey);
        } else {
            this.scheduleClearSpeaking(streamKey);
        }
    };

    // Turn the indicator on right away and cancel any pending "off".
    markSpeaking = (streamKey) => {
        if (this.speakingOffTimers.has(streamKey)) {
            clearTimeout(this.speakingOffTimers.get(streamKey));
            this.speakingOffTimers.delete(streamKey);
        }
        if (!this.state.speakingStreams[streamKey]) {
            this.setState((prev) => ({
                speakingStreams: { ...prev.speakingStreams, [streamKey]: true },
            }));
        }
    };

    // Turn the indicator off after a short pause so it does not flicker between words.
    scheduleClearSpeaking = (streamKey) => {
        if (!this.state.speakingStreams[streamKey] || this.speakingOffTimers.has(streamKey)) {
            return;
        }
        const timerId = setTimeout(() => {
            this.speakingOffTimers.delete(streamKey);
            this.clearSpeaking(streamKey);
        }, SPEAKING_OFF_DELAY);
        this.speakingOffTimers.set(streamKey, timerId);
    };

    clearSpeaking = (streamKey) => {
        this.setState((prev) => {
            if (!prev.speakingStreams[streamKey]) {
                return null;
            }
            const next = { ...prev.speakingStreams };
            delete next[streamKey];
            return { speakingStreams: next };
        });
    };

    // Animated equalizer bars shown next to a speaking participant's name.
    renderSpeakingIndicator = () => {
        return (
            <span className="participant-speaking-indicator" title="Speaking" aria-label="Speaking">
                <span className="participant-speaking-bar"></span>
                <span className="participant-speaking-bar"></span>
                <span className="participant-speaking-bar"></span>
            </span>
        );
    };
    // muteStudentAudio
    muteStudentAudio = (streamId) => {
        this.props.muteStudentAudio(streamId);
    };

    // muteStudentVideo
    muteStudentVideo = (streamId) => {
        this.props.muteStudentVideo(streamId);
    };

    // removeStudentStream
    removeStudentStream = (streamId) => {
        this.props.removeStudentStream(
            streamId,
            this.state.studentName,
            this.state.studentId,
            (response) => {
                if (response) {
                    this.setState({ removeStudentAlert: false });
                }
            }
        );
    };

    //removeStudentAlertModal
    removeStudentAlertModal = () => {
        return (
            <Modal
                isOpen={this.state.removeStudentAlert}
                overlayClassName={'modal-background z-index-1'}
                className={'modal-width-endcall'}
                ariaHideApp={false}
            >
                <div className="relative w-full max-w-2xl max-h-full">
                    <div className="relative p-6 rounded-lg shadow bg-card">
                        <div className="flex items-center justify-between mb-6">
                            <h5 className="text-lg font-medium text-white">
                                Remove Team Member Confirmation
                            </h5>
                            <button
                                type="button"
                                onClick={() => {
                                    this.setState({
                                        removeStudentAlert: !this.state.removeStudentAlert,
                                    });
                                }}
                            >
                                <svg
                                    className="styles_closeIcon__1QwbI"
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="28"
                                    height="28"
                                    viewBox="0 0 36 36"
                                >
                                    <path
                                        d="M28.5 9.62L26.38 7.5 18 15.88 9.62 7.5 7.5 9.62 15.88 18 7.5 26.38l2.12 2.12L18 20.12l8.38 8.38 2.12-2.12L20.12 18z"
                                        fill="white"
                                    ></path>
                                </svg>
                            </button>
                        </div>
                        <div>
                            <div>
                                <span className="text-base text-white">
                                    Are you sure you want to remove the Team Member{' '}
                                    {this.state.studentName}?
                                </span>
                            </div>
                            <div className="flex flex-row items-center justify-end gap-4 mt-6">
                                <button
                                    className="w-full py-2 text-base font-semibold text-white break-normal bg-black rounded-lg sm:w-3/12"
                                    onClick={() => {
                                        this.setState({
                                            removeStudentAlert: !this.state.removeStudentAlert,
                                        });
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="w-full py-2 text-base font-semibold text-white rounded-lg bg-UIprimary sm:w-3/12"
                                    onClick={() => {
                                        this.removeStudentStream(this.state.streamId);
                                    }}
                                >
                                    Yes
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        );
    };

    // renderClassRoomParticipants
    renderClassRoomParticipants = () => {
        return (
            <>
                {/* BUD Image + Text */}
                {/* BUD Row */}
                {/* BUD Row (Aligned properly) */}
                <div className="flex justify-between mb-6 gap-x-6">
                    <div className="flex items-center gap-x-4">
                        {/* Empty avatar space to match users */}
                        <div className="relative">
                            <span className="w-2.5 h-2.5 absolute bottom-0 right-0 rounded-full"></span>
                        </div>
                        <span className="text-base text-white font-medium">
                            {this.props.t('bud').toUpperCase()}
                        </span>
                    </div>

                    <img src={BudImage} alt="Bud" className="w-10 h-10 rounded object-cover" />
                </div>

                {this.props.streams
                    .filter((user) => user.name !== 'screen')
                    .map((user, index) => {
                        this.props.sessionUsers.find((e) => e.name === user.name);
                        const streamKey = this.getStreamKey(user);
                        // Only indicate speaking while the participant's mic is actually on.
                        const isSpeaking =
                            user.hasAudio && !!this.state.speakingStreams[streamKey];
                        return (
                            <div className="flex justify-between mb-6 gap-x-6" key={index}>
                                <div className="flex items-center gap-x-4">
                                    <div className="relative">
                                        <span className="w-2.5 h-2.5 absolute bottom-0 right-0 rounded-full"></span>
                                        {/* <img
                    className="flex-none rounded-full sm:w-12 sm:h-12 w-9 h-9 bg-gray-50"
                    alt=""
                    src={
                      userDetails && userDetails.image
                        ? userDetails.image
                        : profileImg
                    }
                  /> */}
                                    </div>
                                    <span
                                        className={`text-base table-ellipse ${
                                            isSpeaking ? 'font-medium' : 'text-white'
                                        }`}
                                        style={isSpeaking ? { color: '#22c55e' } : undefined}
                                        title={toDisplayName(user.name)}
                                    >
                                        {toDisplayName(user.name)}
                                    </span>
                                    {isSpeaking && this.renderSpeakingIndicator()}
                                </div>
                                <div className="flex items-center gap-x-4">
                                    <button type="button" style={{ cursor: 'default' }}>
                                        {/* {user?.channel[2]?.active && (
                      <iconify-icon
                        icon="ic:outline-back-hand"
                        width="20"
                        height="20"
                        style={{
                          paddingTop: "5px",
                          color: "grey",
                        }}
                      ></iconify-icon>
                    )} */}
                                    </button>
                                    <button
                                        type="button"
                                        style={{
                                            cursor: user.hasVideo ? 'pointer' : 'default',
                                        }}
                                        onClick={() => {
                                            if (user.hasVideo) {
                                                this.muteStudentVideo(user.streamId);
                                            }
                                        }}
                                    >
                                        {user.hasVideo ? (
                                            <iconify-icon
                                                icon="carbon:video-filled"
                                                width="20"
                                                height="20"
                                                style={{
                                                    paddingTop: '5px',
                                                    color: 'white',
                                                }}
                                            ></iconify-icon>
                                        ) : (
                                            <iconify-icon
                                                icon="carbon:video-off-filled"
                                                width="20"
                                                height="20"
                                                style={{
                                                    paddingTop: '5px',
                                                    color: 'white',
                                                }}
                                            ></iconify-icon>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        style={{
                                            cursor: user.hasAudio ? 'pointer' : 'default',
                                        }}
                                        onClick={() => {
                                            if (user.hasAudio) {
                                                this.muteStudentAudio(user.streamId);
                                            }
                                        }}
                                    >
                                        {user.hasAudio ? (
                                            <iconify-icon
                                                icon="ant-design:audio-outlined"
                                                width="20"
                                                height="20"
                                                style={{
                                                    paddingTop: '5px',
                                                    color: 'white',
                                                }}
                                            ></iconify-icon>
                                        ) : (
                                            <iconify-icon
                                                icon="ant-design:audio-muted-outlined"
                                                width="20"
                                                height="20"
                                                style={{
                                                    paddingTop: '5px',
                                                    color: 'white',
                                                }}
                                            ></iconify-icon>
                                        )}
                                    </button>
                                    {/* {userDetails?.stuRollNo && (
                    <button
                      type="button"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        this.setState({
                          removeStudentAlert: true,
                          studentName: user.name,
                          streamId: user.streamId,
                          studentId: userDetails.userId,
                        });
                      }}
                    >
                      <iconify-icon
                        icon="mdi:remove"
                        width="20"
                        height="20"
                        style={{
                          paddingTop: "5px",
                          color: "white",
                        }}
                      ></iconify-icon>
                    </button>
                  )} */}
                                </div>
                            </div>
                        );
                    })}
            </>
        );
    };

    //renderBroadCastParticipants
    renderBroadCastParticipants = () => {
        return (
            <>
                {this.props.participantsDetails &&
                this.props.participantsDetails.activeStudents &&
                this.props.participantsDetails.activeStudents.length ? (
                    <>
                        <h3 className="mb-3 font-semibold">
                            In this session ({this.props.participantsDetails.activeStudents.length})
                        </h3>
                        {this.props.participantsDetails.activeStudents.map((user, index) => {
                            let userImage = this.props.sessionUsers.find(
                                (e) => e.name === user.name
                            );
                            return (
                                <div className="flex justify-between mb-6 gap-x-6" key={index}>
                                    <div className="flex items-center gap-x-4">
                                        <div className="relative">
                                            <span className="bg-success w-2.5 h-2.5 absolute bottom-0 right-0 rounded-full"></span>
                                            <div className="sm:w-12 sm:h-12 w-9 h-9">
                                                <img
                                                    className="flex-none w-full h-full rounded-full bg-gray-50"
                                                    alt=""
                                                    src={
                                                        userImage && userImage.image
                                                            ? userImage.image
                                                            : profileImg
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <span
                                            className="w-32 text-base table-ellipse"
                                            title={user.name}
                                        >
                                            {user.name}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </>
                ) : (
                    ''
                )}
                {this.props.participantsDetails &&
                this.props.participantsDetails.inActiveStudents &&
                this.props.participantsDetails.inActiveStudents.length ? (
                    <>
                        <h3 className="mb-3 font-semibold">
                            Others invited ({this.props.participantsDetails.inActiveStudents.length}
                            )
                        </h3>
                        {this.props.participantsDetails.inActiveStudents.map((user, index) => {
                            let userImage = this.props.sessionUsers.find(
                                (e) => e.name === user.name
                            );
                            return (
                                <div className="flex justify-between mb-6 gap-x-6" key={index}>
                                    <div className="flex items-center gap-x-4">
                                        <div className="relative">
                                            <span className="w-2.5 h-2.5 absolute bottom-0 right-0 rounded-full"></span>
                                            <div className="sm:w-12 sm:h-12 w-9 h-9">
                                                <img
                                                    className="flex-none w-full h-full rounded-full bg-gray-50"
                                                    alt=""
                                                    src={
                                                        userImage && userImage.image
                                                            ? userImage.image
                                                            : profileImg
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <span
                                            className="w-32 text-base table-ellipse"
                                            title={user.name}
                                        >
                                            {user.name}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </>
                ) : (
                    ''
                )}
            </>
        );
    };

    render() {
        return (
            <div className="mx-4 my-6 rounded-lg">
                <style>{`
                    .participant-speaking-indicator {
                        display: inline-flex;
                        align-items: center;
                        gap: 2px;
                        height: 14px;
                    }
                    .participant-speaking-indicator .participant-speaking-bar {
                        width: 3px;
                        height: 100%;
                        background-color: #22c55e;
                        border-radius: 2px;
                        transform: scaleY(0.35);
                        transform-origin: center;
                        animation: participantSpeakingBar 0.9s ease-in-out infinite;
                    }
                    .participant-speaking-indicator .participant-speaking-bar:nth-child(2) {
                        animation-delay: 0.15s;
                    }
                    .participant-speaking-indicator .participant-speaking-bar:nth-child(3) {
                        animation-delay: 0.3s;
                    }
                    @keyframes participantSpeakingBar {
                        0%, 100% { transform: scaleY(0.35); }
                        50% { transform: scaleY(1); }
                    }
                `}</style>
                {this.props.isBroadCast
                    ? this.renderBroadCastParticipants()
                    : this.renderClassRoomParticipants()}
                {this.removeStudentAlertModal()}
            </div>
        );
    }
}

const ConnectedParticipantContent = withLanguage(ParticipantContent);

// Export with LanguageProvider and ref forwarding
export default React.forwardRef((props, ref) => (
    <LanguageProvider>
        <ConnectedParticipantContent {...props} ref={ref} />
    </LanguageProvider>
));
